import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useDeleteVoiceChannel, useRenameVoiceChannel } from '../../hooks/useVoice';
import type { VoiceChannel } from '../../lib/voiceApi';
import { useConfirm } from '../ui/ConfirmProvider';

/**
 * Admin-only: rename a voice channel or delete it. Mirrors GroupSettingsModal,
 * minus members — a voice channel has none, everyone in the company can walk in.
 */
export function VoiceChannelSettingsModal({
  channel,
  onClose,
  onDeleted,
}: {
  channel: VoiceChannel;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const rename = useRenameVoiceChannel();
  const remove = useDeleteVoiceChannel();
  const [name, setName] = useState(channel.name);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed === channel.name) {
      onClose();
      return;
    }
    setError(null);
    rename.mutate(
      { id: channel.id, name: trimmed },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  const destroy = async () => {
    if (
      !(await confirm({
        message: t('teamChat.confirmDeleteVoiceChannel', { name: channel.name }),
        danger: true,
      }))
    ) {
      return;
    }
    remove.mutate(channel.id, {
      onSuccess: () => {
        onClose();
        onDeleted();
      },
      onError: (err) => setError(err instanceof Error ? err.message : String(err)),
    });
  };

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('teamChat.voiceChannelSettings')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="ui-modal-panel ui-modal-compact w-full max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h2 className="font-heading text-base font-semibold text-foreground">
          {t('teamChat.voiceChannelSettings')}
        </h2>
        <label className="ui-field-label mt-3 block" htmlFor="voice-channel-rename">
          {t('teamChat.voiceChannelName')}
        </label>
        <input
          id="voice-channel-rename"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          className="ui-input"
          autoFocus
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => void destroy()}
            disabled={remove.isPending}
            className="ui-btn-danger disabled:opacity-50"
          >
            {t('teamChat.deleteVoiceChannel')}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={rename.isPending || !name.trim()}
              className="ui-btn-primary disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
